import { useState } from "react";
import { useTranslation } from "react-i18next";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import styles from "./styles.module.scss";
import { IScanPageVM } from "./typings";

interface ThumbProps {
  page: IScanPageVM;
  position: number;
  total: number;
  onToggle: () => void;
  onRotate: () => void;
  onRemove: () => void;
  onReorder: (from: number, to: number) => void;
  onPreview: () => void;
}

const PageThumb = ({
  page,
  position,
  total,
  onToggle,
  onRotate,
  onRemove,
  onReorder,
  onPreview,
}: ThumbProps) => {
  const { t } = useTranslation();
  const [over, setOver] = useState(false);

  return (
    <div
      className={`${styles.tile} ${page.selected ? styles.tileSelected : ""} ${
        over ? styles.tileOver : ""
      }`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", String(position));
        e.dataTransfer.effectAllowed = "move";
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (!over) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const from = Number(e.dataTransfer.getData("text/plain"));
        if (!Number.isNaN(from)) onReorder(from, position);
      }}
    >
      <div className={styles.tileTop}>
        <Checkbox
          size="small"
          checked={page.selected}
          onChange={onToggle}
          className={styles.tileCheck}
        />
        <span className={styles.tileNo}>{position + 1}</span>
        <div className={styles.tileActions}>
          <Tooltip title={t("scanner.rotate")}>
            <IconButton size="small" onClick={onRotate}>
              <RotateRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("scanner.remove")}>
            <IconButton size="small" onClick={onRemove}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      <div
        className={styles.tileImgBox}
        onDoubleClick={onPreview}
        title={t("scanner.previewHint")}
      >
        <img
          src={page.thumbnailUrl}
          alt={`${t("scanner.page")} ${position + 1}`}
          className={styles.tileImg}
          style={{ transform: `rotate(${page.rotation}deg)` }}
          draggable={false}
        />
      </div>

      <div className={styles.tileMove}>
        <IconButton
          size="small"
          disabled={position === 0}
          onClick={() => onReorder(position, position - 1)}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          disabled={position === total - 1}
          onClick={() => onReorder(position, position + 1)}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </div>
    </div>
  );
};

export default PageThumb;
